import React, { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import QRCode from 'qrcode';
import { apiUrl } from '../api';
import { CANVAS, WidgetContent, emptyTemplate } from './TemplateRenderer';
import { Logo } from '../App';

const API_BASE_URL = (process.env.REACT_APP_SERVER_URL || '').replace(/\/$/, '');
const SOCKET_URL = API_BASE_URL || undefined;

const STORAGE_UUID = 'puttv_display_uuid';
const STORAGE_SECRET = 'puttv_display_secret';
const STORAGE_PAIRING = 'puttv_display_pairing';
// Last thing this screen rendered, so a reload repaints instead of blanking.
const STORAGE_LAST_RENDER = 'puttv_display_last_render';

// An identity handed to us in the URL as ?identity=<uuid>.<secret>, used by the
// dashboard wizard to open several independent screens in one browser. Without
// it every frame on this origin would share the one localStorage identity and
// therefore be the same device — which is exactly what the wizard's two-screen
// group push must not be. A supplied identity is already claimed, so these
// frames skip provisioning, pairing and localStorage entirely.
function readSuppliedIdentity() {
  const raw = new URLSearchParams(window.location.search).get('identity');
  if (!raw) return null;
  const sep = raw.indexOf('.');
  if (sep < 1) return null;
  const deviceUUID = raw.slice(0, sep);
  const deviceSecret = raw.slice(sep + 1);
  return deviceSecret ? { deviceUUID, deviceSecret } : null;
}

// Reload the page if the server has been silent this long. Comfortably longer
// than any normal reconnect backoff (max 10s) or venue wifi outage, so a
// healthy screen never reloads; short enough that a wedged one recovers
// without a site visit.
const WATCHDOG_MS = 5 * 60 * 1000;

// Reachable at /display — the "load this on any smart TV / browser" kiosk
// entry point. Self-provisions its own device identity in localStorage on
// first load (exactly like a fresh physical device does with a freshly
// generated machine-id), so the URL never needs a UUID or secret in it: any
// number of browser tabs/TVs can all point at the same /display URL and each
// becomes its own independent device the first time it loads.
export default function DisplayView() {
  const [phase, setPhase] = useState('provisioning'); // provisioning | pairing | connecting | live | error
  const [errorMsg, setErrorMsg] = useState('');
  const [pairUrl, setPairUrl] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [template, setTemplate] = useState(emptyTemplate());
  const [playlist, setPlaylist] = useState(null); // [{json, duration}] | null
  const [playlistIndex, setPlaylistIndex] = useState(0);
  const [liveData, setLiveData] = useState({}); // sourceId -> data
  const [scale, setScale] = useState(1);

  const identityRef = useRef(null); // {deviceUUID, deviceSecret}
  const suppliedIdentityRef = useRef(false); // identity came from the URL (wizard frame)
  const socketRef = useRef(null);
  const pollRef = useRef(null);
  const visibilityCleanupRef = useRef(null);
  const playlistTimerRef = useRef(null);
  // Once a screen has shown content, a later disconnect must not replace it
  // with the setup card — see the 'disconnect' handler.
  const hasBeenLiveRef = useRef(false);
  // Last time the server said anything at all; drives the reload watchdog.
  const lastContactRef = useRef(Date.now());

  // Fit the fixed 1920x1080 canvas to whatever window the browser gives us,
  // letterboxed and centered — a TV browser's actual viewport is unknown.
  useEffect(() => {
    const fit = () => setScale(Math.min(window.innerWidth / CANVAS.w, window.innerHeight / CANVAS.h));
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  // Step 1: get (or mint) a device identity, then start pairing/connecting.
  useEffect(() => {
    (async () => {
      try {
        const supplied = readSuppliedIdentity();
        let deviceUUID, deviceSecret;

        if (supplied) {
          ({ deviceUUID, deviceSecret } = supplied);
          suppliedIdentityRef.current = true;
        } else {
          deviceUUID = localStorage.getItem(STORAGE_UUID);
          deviceSecret = localStorage.getItem(STORAGE_SECRET);
          if (!deviceUUID || !deviceSecret) {
            deviceUUID = crypto.randomUUID();
            const res = await fetch(apiUrl('/api/device/generate-secret'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ deviceUUID }),
            });
            if (!res.ok) throw new Error(`Could not provision this display (${res.status})`);
            const data = await res.json();
            deviceSecret = data.deviceSecret;
            localStorage.setItem(STORAGE_UUID, deviceUUID);
            localStorage.setItem(STORAGE_SECRET, deviceSecret);
          }
        }

        identityRef.current = { deviceUUID, deviceSecret };
        // Repaint the last known content before touching the network, so a
        // reload (watchdog, power cut, TV browser restart) shows the board again
        // immediately instead of a setup card.
        hydrateLastRender(deviceUUID);
        // A supplied identity is claimed by definition — nothing to check.
        if (supplied) { connectSocket(); return; }
        await checkPairedThenConnect();
      } catch (e) {
        // Never trade working content for an error card: a screen showing a menu
        // board through an outage is more useful than an accurate status
        // message, and the socket retries on its own once the network returns.
        if (hasBeenLiveRef.current && identityRef.current) { connectSocket(); return; }
        setPhase('error');
        setErrorMsg(e.message || 'Failed to set up this display');
      }
    })();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (visibilityCleanupRef.current) visibilityCleanupRef.current();
      if (playlistTimerRef.current) clearTimeout(playlistTimerRef.current);
      if (socketRef.current) socketRef.current.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function checkPairedThenConnect() {
    const { deviceUUID } = identityRef.current;
    const res = await fetch(apiUrl(`/api/device/${encodeURIComponent(deviceUUID)}`));
    const info = await res.json();
    if (info.paired) {
      connectSocket();
    } else {
      startPairing();
    }
  }

  async function startPairing() {
    setPhase('pairing');

    // Reuse the last-issued code across reloads if it hasn't expired — a TV
    // browser refreshing (or just being slow to load) shouldn't invalidate a
    // QR someone already has their phone camera pointed at.
    let token, url;
    const stored = localStorage.getItem(STORAGE_PAIRING);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.expiresAt > Date.now()) { token = parsed.token; url = parsed.pairUrl; }
      } catch (_) { /* corrupt entry — fall through to minting a fresh one */ }
    }

    if (!token) {
      const { deviceUUID, deviceSecret } = identityRef.current;
      const res = await fetch(apiUrl('/api/pair/start'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceUUID, deviceSecret }),
      });
      if (!res.ok) throw new Error('Could not start pairing');
      const data = await res.json();
      token = data.token; url = data.pairUrl;
      localStorage.setItem(STORAGE_PAIRING, JSON.stringify({
        token, pairUrl: url, expiresAt: Date.now() + data.expiresInSeconds * 1000,
      }));
    }

    setPairUrl(url);
    try { setQrDataUrl(await QRCode.toDataURL(url, { margin: 1, width: 360 })); } catch (_) { /* text fallback still shown */ }

    const checkStatus = async () => {
      try {
        const r = await fetch(apiUrl(`/api/pair/status?token=${encodeURIComponent(token)}`));
        const s = await r.json();
        if (s.status === 'claimed') {
          teardownPoll();
          localStorage.removeItem(STORAGE_PAIRING);
          connectSocket();
        } else if (s.status === 'expired') {
          teardownPoll();
          localStorage.removeItem(STORAGE_PAIRING);
          startPairing(); // fetch a fresh token/QR rather than dead-end the screen
        }
      } catch (_) { /* transient network blip — keep polling */ }
    };

    // A backgrounded/unfocused tab (e.g. the TV screen sitting idle while you
    // point a phone camera at it) throttles or pauses setInterval, so this
    // token could silently expire without the interval ever getting to swap
    // in a fresh QR. Re-check immediately whenever the tab becomes visible
    // again, rather than trusting the interval alone.
    const onVisible = () => { if (!document.hidden) checkStatus(); };
    document.addEventListener('visibilitychange', onVisible);
    visibilityCleanupRef.current = () => document.removeEventListener('visibilitychange', onVisible);

    function teardownPoll() {
      clearInterval(pollRef.current);
      visibilityCleanupRef.current();
      visibilityCleanupRef.current = null;
    }

    pollRef.current = setInterval(checkStatus, 3000);
  }

  // ---- last-render cache ----------------------------------------------
  // The watchdog reload (and any power cycle) drops React state, so without
  // this a screen that was correctly showing a board comes back up on the setup
  // card and stays there until the server re-pushes — which never happens while
  // the network is still down. Keyed by device so a re-provisioned screen can't
  // inherit the previous device's content.

  function rememberLastRender(entry) {
    try {
      const { deviceUUID } = identityRef.current || {};
      if (!deviceUUID) return;
      if (!entry) { localStorage.removeItem(STORAGE_LAST_RENDER); return; }
      localStorage.setItem(STORAGE_LAST_RENDER, JSON.stringify({ deviceUUID, ...entry }));
    } catch (_) {
      // Over quota (a template carrying big data-URI images) or storage
      // disabled — caching is an optimisation, so drop it silently.
    }
  }

  function hydrateLastRender(deviceUUID) {
    try {
      const raw = localStorage.getItem(STORAGE_LAST_RENDER);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved.deviceUUID !== deviceUUID) return;
      if (saved.playlist && saved.playlist.length) startPlaylistFrom(saved.playlist, 0);
      else if (saved.template) setTemplate(saved.template);
      else return;
      // Treat restored content as having been live: a later disconnect must not
      // pull it back to the setup card either.
      hasBeenLiveRef.current = true;
      setPhase('live');
    } catch (_) { /* corrupt entry — fall through to normal setup */ }
  }

  // Going live is the point after which the screen must never fall back to
  // the setup card on a transient disconnect.
  function goLive() {
    hasBeenLiveRef.current = true;
    lastContactRef.current = Date.now();
    setPhase('live');
  }

  function connectSocket() {
    setPhase('connecting');
    const { deviceUUID, deviceSecret } = identityRef.current;
    const socket = io(SOCKET_URL, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      timeout: 45000,
    });
    socketRef.current = socket;

    // Any traffic at all counts as proof the link is alive, so the watchdog
    // only fires on genuine silence rather than on "no content pushed today".
    socket.onAny(() => { lastContactRef.current = Date.now(); });

    socket.on('connect', () => {
      lastContactRef.current = Date.now();
      socket.emit('identify', { clientType: 'display', deviceUUID, deviceSecret });
    });

    socket.on('auth_error', () => {
      socket.disconnect();
      rememberLastRender(null);
      if (suppliedIdentityRef.current) {
        // A wizard screen whose demo device was reaped or reset. There is no
        // identity of its own to re-provision — the dashboard owns that.
        setPhase('error');
        setErrorMsg('This demo screen has expired. Restart the tour to get a new one.');
        return;
      }
      // The stored secret no longer matches (device unclaimed/wiped server-side).
      // Drop it and self-provision a fresh identity rather than loop forever.
      localStorage.removeItem(STORAGE_UUID);
      localStorage.removeItem(STORAGE_SECRET);
      setPhase('error');
      setErrorMsg('This display was unlinked from its account. Reload the page to re-pair.');
    });

    socket.on('paired', () => goLive());

    // The server acks a successful 'display' identify with a plain 'message'
    // event — this is the ONLY signal that identify actually succeeded when
    // the device has nothing assigned to it yet (a freshly claimed screen
    // with no template pushed gets no display_command and no 'paired' event,
    // since 'paired' is only broadcast to whichever socket was already open
    // at the moment someone else claims it — this socket connects afterward).
    // Without this, a newly paired but content-less display would sit on
    // "Connecting to your account…" forever despite being online.
    socket.on('message', () => goLive());

    socket.on('display_command', (data) => {
      goLive();
      const { command, params } = data || {};
      if (command === 'show_template') {
        stopPlaylistTimer();
        setPlaylist(null);
        setTemplate(params || emptyTemplate());
        rememberLastRender({ template: params || emptyTemplate() });
      } else if (command === 'show_playlist') {
        setTemplate(emptyTemplate());
        startPlaylistFrom(params && params.playlist, 0);
        rememberLastRender({ playlist: (params && params.playlist) || [] });
      } else if (command === 'clear_template' || command === 'stop_playlist') {
        stopPlaylistTimer();
        setPlaylist(null);
        setTemplate(emptyTemplate());
        rememberLastRender(null);
      }
    });

    socket.on('data_source_update', ({ sourceId, data }) => {
      setLiveData((prev) => ({ ...prev, [sourceId]: data }));
    });

    // A disconnect must NOT wipe the screen. Signage content stays valid
    // through a network blip, and a status card is never more useful to a
    // venue's customers than the menu board they were reading. Once we have
    // shown content we keep showing it and reconnect silently; only a screen
    // that has never been live falls back to the setup card.
    socket.on('disconnect', () => {
      setPhase((p) => (hasBeenLiveRef.current ? p : 'connecting'));
    });
  }

  // ---- kiosk watchdog -------------------------------------------------
  // A screen runs unattended for weeks. If the page wedges (stale socket that
  // never fires reconnect, an exception that kills the render loop, memory
  // bloat) nothing recovers it and the venue just sees a frozen display until
  // someone power-cycles the box. Reload if we have had no contact with the
  // server for WATCHDOG_MS. Any socket traffic pushes the deadline out.
  useEffect(() => {
    const tick = setInterval(() => {
      const silent = Date.now() - lastContactRef.current;
      if (silent > WATCHDOG_MS) {
        console.warn(`[display] no server contact for ${Math.round(silent / 1000)}s — reloading`);
        window.location.reload();
      }
    }, 30000);
    return () => clearInterval(tick);
  }, []);

  // ---- keep the panel awake -------------------------------------------
  // Without a wake lock the browser/OS is free to blank the display on its
  // idle timer — there is no pointer or key activity on a signage screen, so
  // it looks idle forever. Re-acquired on visibility change because the lock
  // is dropped whenever the page is hidden. Best-effort: unsupported browsers
  // (and non-secure contexts) just skip it.
  useEffect(() => {
    let lock = null;
    let cancelled = false;
    const acquire = async () => {
      try {
        if (!('wakeLock' in navigator) || document.hidden || cancelled) return;
        lock = await navigator.wakeLock.request('screen');
        lock.addEventListener('release', () => { lock = null; });
      } catch (_) { /* not supported / denied — the OS kiosk mode may handle it */ }
    };
    acquire();
    const onVis = () => { if (!document.hidden) acquire(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVis);
      if (lock) { try { lock.release(); } catch (_) { /* already gone */ } }
    };
  }, []);

  function stopPlaylistTimer() {
    if (playlistTimerRef.current) { clearTimeout(playlistTimerRef.current); playlistTimerRef.current = null; }
  }

  function startPlaylistFrom(items, index) {
    stopPlaylistTimer();
    if (!items || items.length === 0) return;
    setPlaylist(items);
    setPlaylistIndex(index % items.length);
    const item = items[index % items.length];
    playlistTimerRef.current = setTimeout(
      () => startPlaylistFrom(items, index + 1),
      Math.max(1, (item.duration || 10)) * 1000
    );
  }

  // The template actually on screen right now: either the single pushed
  // template, or the current playlist item.
  const current = playlist ? playlist[playlistIndex].json : template;
  const widgets = (current.widgets || []).map((w) =>
    w.type === 'datafeed' && w.props && w.props.sourceId && liveData[w.props.sourceId]
      ? { ...w, props: { ...w.props, _liveData: liveData[w.props.sourceId] } }
      : w
  );

  if (phase !== 'live') {
    return <SetupScreen phase={phase} errorMsg={errorMsg} pairUrl={pairUrl} qrDataUrl={qrDataUrl} />;
  }

  return (
    <div style={{ width: '100vw', height: '100vh', background: '#000', overflow: 'hidden' }}>
      <div style={{
        width: CANVAS.w, height: CANVAS.h, transform: `scale(${scale})`, transformOrigin: 'top left',
        position: 'absolute', left: '50%', top: '50%', marginLeft: -CANVAS.w / 2 * scale, marginTop: -CANVAS.h / 2 * scale,
        background: current.background || '#000',
      }}>
        {widgets.map((w, i) => (
          <div key={w.id || i} style={{ position: 'absolute', left: w.x, top: w.y, width: w.w, height: w.h }}>
            <WidgetContent widget={w} />
          </div>
        ))}
        {current.watermark && (
          <div style={{
            position: 'absolute', right: 32, bottom: 24, fontSize: 56, fontWeight: 700,
            letterSpacing: '0.04em', color: 'rgba(255,255,255,0.6)', textShadow: '0 2px 6px rgba(0,0,0,0.7)',
            pointerEvents: 'none',
          }}>
            puttv.io
          </div>
        )}
        {widgets.length === 0 && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'rgba(255,255,255,0.35)', fontSize: 28, fontWeight: 500,
          }}>
            Connected — waiting for content
          </div>
        )}
      </div>
    </div>
  );
}

function SetupScreen({ phase, errorMsg, pairUrl, qrDataUrl }) {
  return (
    <div className="auth-wrap">
      <div className="auth-card" style={{ maxWidth: 460 }}>
        <div className="auth-brand">
          <Logo size={34} />
          <span className="brand-name">PUTTV</span>
        </div>
        {phase === 'provisioning' && <p className="tagline">Setting up this display…</p>}
        {phase === 'connecting' && <p className="tagline">Connecting to your account…</p>}
        {phase === 'error' && <p className="tagline">{errorMsg}</p>}
        {phase === 'pairing' && (
          <>
            <p className="tagline">Scan this code with your phone to add this screen to your account.</p>
            {qrDataUrl
              ? <img src={qrDataUrl} alt="Pairing QR code" style={{ width: 220, height: 220, borderRadius: 12 }} />
              : <p style={{ wordBreak: 'break-all', fontSize: 13 }}>{pairUrl}</p>}
            <p className="tagline" style={{ marginTop: 16, fontSize: 12 }}>
              No phone handy? Open <b>{pairUrl}</b> on any signed-in device.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
